import { Dialog, DialogPanel } from "@headlessui/react";
import { IoMdClose } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { commonToast } from "../../helper/Toast";
import Loading from "../Loading";
import {
  DEFAULT_ADMIN_PASSWORD,
  createContentCreator,
  createStudent,
  createTeacher,
  getApiError,
  mapPreferredLanguage,
} from "../../helper/adminUsersApi";

const ROLE_CONFIG = {
  student: {
    titleKey: "add_student_title",
    successKey: "student_added_successfully",
    fallbackTitle: "Add New Student",
    fallbackSuccess: "Student added successfully!",
  },
  teacher: {
    titleKey: "teacher_management.add_teacher",
    successKey: "teacher_management.add_success",
    fallbackTitle: "Add Teacher",
    fallbackSuccess: "Teacher added successfully!",
  },
  creator: {
    titleKey: "creator_management.add_creator",
    successKey: "creator_management.add_success",
    fallbackTitle: "Add Creator",
    fallbackSuccess: "Creator added successfully!",
  },
};

export default function AddAdminUserModel({
  open,
  setOpen,
  onCreated,
  role = "student",
  schoolId,
}) {
  const { t, i18n } = useTranslation();
  const config = ROLE_CONFIG[role] || ROLE_CONFIG.student;

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    password: DEFAULT_ADMIN_PASSWORD,
    gender: "UNSPECIFIED",
    birthDate: "",
    nickname: "",
    subjectsToTeach: "",
  };

  const validationSchema = Yup.object({
    firstName: Yup.string().required(t("validation.name_required")),
    lastName: Yup.string().required(t("validation.lastname_required")),
    email: Yup.string()
      .email(t("validation.email_invalid"))
      .required(t("validation.email_required")),
    password: Yup.string().required(t("validation.password_required")),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const shared = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        password: values.password,
        preferredLanguage: mapPreferredLanguage(i18n.language),
        countryCode: "LT",
        userPreferences: {
          preferVisionImpairmentMode: false,
          allowMarketingDataCollection: false,
          allowAnalyticsDataCollection: false,
        },
      };

      if (role === "teacher") {
        await createTeacher({
          ...shared,
          schoolIds: schoolId ? [schoolId] : [],
          subjectsToTeach: values.subjectsToTeach
            ? values.subjectsToTeach.split(",").map((s) => s.trim()).filter(Boolean)
            : [],
        });
      } else if (role === "creator") {
        await createContentCreator(shared);
      } else {
        const studentPayload = {
          ...shared,
          schoolId: schoolId || undefined,
          gender: values.gender || "UNSPECIFIED",
        };
        if (values.birthDate) studentPayload.birthDate = values.birthDate;
        if (values.nickname) studentPayload.nickname = values.nickname.trim();
        await createStudent(studentPayload);
      }

      commonToast(t(config.successKey) || config.fallbackSuccess, "success");
      onCreated?.();
      resetForm();
      setOpen(false);
    } catch (error) {
      commonToast(getApiError(error, t("updateFailed") || "Failed to add user"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      as="div"
      className="relative z-40 focus:outline-none"
      onClose={() => setOpen(false)}
    >
      <div className="fixed inset-0 bg-black/40" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded-2xl bg-white p-6">
            <Formik
              key={`${role}-${open}`}
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900">
                      {t(config.titleKey) || config.fallbackTitle}
                    </h3>
                    <IoMdClose
                      className="cursor-pointer text-gray-500 hover:text-gray-800"
                      fontSize={24}
                      onClick={() => setOpen(false)}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-y-3">
                    <InputField label={t("first_name") || t("name")} name="firstName" />
                    <InputField label={t("last_name") || t("lastname")} name="lastName" />
                    <InputField label={t("email")} name="email" type="email" />
                    <InputField label={t("password")} name="password" type="password" />

                    {role === "student" && (
                      <>
                        <InputField label={t("birth_date") || "Birth date"} name="birthDate" type="date" />
                        <InputField label={t("nickname") || "Nickname"} name="nickname" />
                      </>
                    )}

                    {role === "teacher" && (
                      <InputField
                        label={t("teacher_management.subjects") || "Subjects to teach"}
                        name="subjectsToTeach"
                        placeholder="Mathematics, Physics"
                      />
                    )}
                  </div>

                  <div className="mt-6 flex justify-end gap-4">
                    <button type="button" className="secondary-btn" onClick={() => setOpen(false)}>
                      {t("cancel")}
                    </button>
                    <button type="submit" className="primary-btn" disabled={isSubmitting}>
                      {isSubmitting ? <Loading size={5} /> : t("add") || "Add"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

const InputField = ({ label, name, type = "text", placeholder }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <Field
      type={type}
      name={name}
      placeholder={placeholder}
      autoComplete="off"
      className="border h-[45px] w-full rounded-lg px-3 shadow-inner outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
    />
    <ErrorMessage name={name} component="div" className="text-red-500 text-xs mt-1" />
  </div>
);
